// ── 4 Modelle ── der Abschnitt „Flow-Modelle": Schalter, Monatslimit des
// eigenen Flow-Keys und die Liste der Modelle (Name, Art, Modell-ID,
// Beschreibung). Die Modell-ID ist freier Text mit Vorschlägen: für
// Sprachmodelle der Katalog von Requesty (erst, wenn der Abschnitt an ist),
// für Entscheidungen die drei Entscheidungsmodelle von Requesty.

import { useQuery } from "@tanstack/react-query";
import type { Dispatch, SetStateAction } from "react";
import { get } from "../../api/client";
import { keys } from "../../api/queries";
import type { Catalog, GatewayModelsResponse, StepCheck, Tenant } from "../../api/types";
import { Note } from "../../ui/badge";
import { Check, Field, Input } from "../../ui/field";
import { SelectMenu } from "../../ui/select-menu";
import {
  DECISION_MODEL_SUGGESTIONS, FLOW_KIND, addFlowModel, bodyIndexOf, modelSuggestions, removeFlowModel, updateFlowModel,
  type FlowModelRow, type FlowsState,
} from "./flow-models";
import { fieldError } from "./shared";

export function FlowModelsSection<S extends FlowsState>({ tenant: t, catalog: cat, state, setState, check }: {
  tenant: Tenant;
  catalog: Catalog;
  state: S;
  setState: Dispatch<SetStateAction<S>>;
  check: StepCheck | null;
}) {
  const err = (k: string) => fieldError(check, k);
  const gw = cat.platform_gateway;
  const rows = state.f_models;
  const setRows = (fn: (rows: FlowModelRow[]) => FlowModelRow[]) => setState((s) => ({ ...s, f_models: fn(s.f_models) }));
  // Derselbe Katalog wie im Modell-Picker des Plattform-Keys (gleicher Schlüssel, gleiche Haltbarkeit).
  const catalog = useQuery({
    queryKey: keys.gatewayModels(t.id, "platform"),
    queryFn: () => get<GatewayModelsResponse>(`/tenants/${t.id}/gateway-models?which=platform`),
    enabled: Boolean(gw) && state.f_enabled,
    staleTime: Infinity,
    gcTime: 0,
  });
  const llmIds = (catalog.data?.models ?? []).map((m) => m.id);
  const listId = (kind: string) => `pf-flow-models-${kind}-${t.id}`;
  return (
    <div className="pf-integration">
      <div className="pf-integration__head">
        <Check
          name="f_enabled" checked={state.f_enabled} disabled={!gw && !state.f_enabled}
          onChange={(v) => setState((s) => ({ ...s, f_enabled: v }))}
          label={<strong>Flow-Modelle</strong>}
          hint={gw
            ? "Modelle für die KI- und Entscheidungsschritte der Flows — über einen eigenen Requesty-Key mit eigenem Monatslimit, nie über den Key der Agenten. Welche Rolle welches Modell benutzen darf, stellt der Tenant-Admin in der Instanz ein."
            : "Aus: REQUESTY_MANAGEMENT_KEY fehlt in der Plattform."}
        />
      </div>
      <div className="pf-integration__fields pf-list" hidden={!state.f_enabled}>
        <div className="pf-grid">
          <Field label="Limit des Flow-Keys je Monat (USD)" error={err("flows.budget_usd")}>
            <Input
              name="f_budget" type="number" min={1} step={1} value={state.f_budget} invalid={!!err("flows.budget_usd")}
              onChange={(e) => { const v = e.target.value; setState((s) => ({ ...s, f_budget: v })); }}
            />
          </Field>
        </div>
        {rows.map((r, i) => {
          const idx = bodyIndexOf(rows, r.rid);
          const modelErr = idx >= 0 ? err(`flows.models.${idx}.model`) : "";
          const n = i + 1;
          return (
            <div className="pf-flowm" key={r.rid} data-flow-model={r.key || "neu"}>
              <div className="pf-flowm__fields">
                <Field label="Name">
                  <Input name={`flow_name_${n}`} aria-label={`Name (Flow-Modell ${n})`} value={r.name} maxLength={80}
                    onChange={(e) => { const v = e.target.value; setRows((x) => updateFlowModel(x, r.rid, { name: v })); }} />
                </Field>
                <Field label="Art">
                  <SelectMenu name={`flow_kind_${n}`} ariaLabel={`Art (Flow-Modell ${n})`} value={r.kind} options={FLOW_KIND}
                    onChange={(v) => setRows((x) => updateFlowModel(x, r.rid, { kind: v === "decision" ? "decision" : "llm" }))} />
                </Field>
                <Field label="Modell-ID" error={modelErr}>
                  <Input name={`flow_model_${n}`} aria-label={`Modell-ID (Flow-Modell ${n})`} value={r.model} maxLength={200} invalid={!!modelErr}
                    list={listId(r.kind)} placeholder={r.kind === "decision" ? DECISION_MODEL_SUGGESTIONS[0] : "z. B. openai/gpt-6-luna"} autoComplete="off"
                    onChange={(e) => { const v = e.target.value; setRows((x) => updateFlowModel(x, r.rid, { model: v })); }} />
                </Field>
                <Field label="Beschreibung" hint={r.key ? <>Schlüssel in der Instanz: <code>{r.key}</code></> : undefined}>
                  <Input name={`flow_description_${n}`} aria-label={`Beschreibung (Flow-Modell ${n})`} value={r.description} maxLength={500}
                    onChange={(e) => { const v = e.target.value; setRows((x) => updateFlowModel(x, r.rid, { description: v })); }} />
                </Field>
              </div>
              <button type="button" className="pf-btn pf-btn--sm pf-btn--ghost pf-btn--danger" aria-label={`Flow-Modell ${n} entfernen`}
                onClick={() => setRows((x) => removeFlowModel(x, r.rid))}>Entfernen</button>
            </div>
          );
        })}
        {!rows.length && <div className="pf-empty">Noch kein Flow-Modell.</div>}
        <div className="pf-row">
          <button type="button" className="pf-btn pf-btn--sm" onClick={() => setRows((x) => addFlowModel(x, "llm"))}>Sprachmodell hinzufügen</button>
          <button type="button" className="pf-btn pf-btn--sm" onClick={() => setRows((x) => addFlowModel(x, "decision"))}>Entscheidungsmodell hinzufügen</button>
        </div>
        {err("flows.models") && <Note tone="error">{err("flows.models")}</Note>}
        {err("flows.enabled") && <Note tone="error">{err("flows.enabled")}</Note>}
        {catalog.data?.error && <Note tone="warn">Keine Vorschläge aus dem Katalog — die Modell-ID lässt sich trotzdem eintippen.</Note>}
        <datalist id={listId("llm")}>{modelSuggestions("llm", llmIds).map((id) => <option key={id} value={id} />)}</datalist>
        <datalist id={listId("decision")}>{modelSuggestions("decision", llmIds).map((id) => <option key={id} value={id} />)}</datalist>
      </div>
    </div>
  );
}
